import { ErrorModal, MESApiService } from "@dexteel/mesf-core";
import { Autocomplete, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useEffect, useState } from "react";

const getCrews = async () => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[MES].[GetShiftsAndCrews]", []);
	if (resp.ok) {
		return get(resp, "data.tables[1].rows", []) as String[];
	}
	throw new Error(resp.message || "Error fetching crews");
};

type Props = {
	value: string | null;
	onChange: (value: string | null) => void;
};

export const CrewsFilter = ({ value, onChange }: Props) => {
	const [error, setError] = useState("");

	const { data: crewsList, isLoading } = useQuery<String[], Error>({
		queryKey: ["crews"],
		queryFn: () => getCrews(),
	});

	const options = [
		{ label: "All", value: "all" },
		...(Array.isArray(crewsList)
			? crewsList.map((shift: any) => ({
					label: shift.Crew,
					value: shift.Crew,
				}))
			: []),
	];

	const selectedOption =
		options.find((option) => option.value === (value || "all")) || options[0];

	// A crew that is not offered (e.g. from a shared link) would show "All" while still
	// filtering by it, so it is cleared instead.
	useEffect(() => {
		if (isLoading || !Array.isArray(crewsList) || !value) return;
		if (!options.some((option) => option.value === value)) onChange(null);
	}, [isLoading, crewsList, value]);

	return (
		<>
			<Autocomplete
				value={selectedOption}
				onChange={(event, newValue) => {
					if (newValue) {
						onChange(newValue.value === "all" ? null : newValue.value);
					}
				}}
				options={options}
				getOptionLabel={(option) => option.label}
				disabled={isLoading}
				disablePortal
				disableClearable
				renderInput={(params) => (
					<TextField
						{...params}
						label="Crew"
						variant="outlined"
						size="small"
						fullWidth
					/>
				)}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
